import { isValidElement } from 'react';
import { InputTableCellParams, InputTableColumn } from '../types';

export interface ValueResolutionOptions {
  rowData: any;
  columnField: string;
  columns: InputTableColumn[];
  getRowId?: (row: any) => string | number;
}

export interface ResolvedValue {
  value: unknown;
  columnField: string;
  columnValue: unknown;
}

/**
 * Utility class for resolving cell values from various sources
 */
export class CellValueResolver {
  /**
   * Resolves the display value for a specific cell
   */
  static resolveValue(options: ValueResolutionOptions): ResolvedValue {
    const { rowData, columnField, columns, getRowId } = options;
    const rowRecord = rowData as Record<string, unknown>;

    if (!columnField) {
      return {
        value: rowRecord,
        columnField: null as any,
        columnValue: undefined
      };
    }

    const columnDef = columns.find(column => column.field === columnField);
    const resolvedRowId = this.getRowId(rowData, getRowId);
    const rawValue = rowRecord[columnField];

    let resolvedValue = this.applyValueGetters(rawValue, columnDef, rowData, columnField, resolvedRowId);
    resolvedValue = this.applyValueFormatters(resolvedValue, columnDef, rowData);
    resolvedValue = this.applyRenderCell(resolvedValue, columnDef, rowData, columnField, resolvedRowId, rawValue);

    return {
      value: { [columnField]: resolvedValue },
      columnField,
      columnValue: resolvedValue
    };
  }

  /**
   * Gets the row ID using the provided getRowId function or fallback logic
   */
  private static getRowId(rowData: any, getRowId?: (row: any) => string | number): string {
    if (typeof getRowId === 'function') {
      return String(getRowId(rowData));
    }

    const rowRecord = rowData as Record<string, unknown>;
    return (rowRecord?.uuid ?? rowRecord?.id ?? rowRecord?.code ?? '') as string;
  }

  /**
   * Applies valueGetter if available
   */
  private static applyValueGetters(
    rawValue: unknown,
    columnDef: InputTableColumn | undefined,
    rowData: any,
    columnField: string,
    resolvedRowId: string
  ): unknown {
    if (!columnDef?.valueGetter) {
      return rawValue;
    }

    try {
      return columnDef.valueGetter(rowData as never, columnField, rawValue as never, resolvedRowId as never);
    } catch (error) {
      console.warn(`Failed to resolve valueGetter for field "${columnField}"`, error);
      return rawValue;
    }
  }

  /**
   * Applies valueFormatter if available
   */
  private static applyValueFormatters(value: unknown, columnDef: InputTableColumn | undefined, rowData: any): unknown {
    if (!columnDef?.valueFormatter) {
      return value;
    }

    try {
      return columnDef.valueFormatter(value as never, rowData, columnDef, null as any);
    } catch (error) {
      console.warn(`Failed to resolve valueFormatter for field "${columnDef.field}"`, error);
      return value;
    }
  }

  /**
   * Applies renderCell and extracts the display value
   */
  private static applyRenderCell(
    value: unknown,
    columnDef: InputTableColumn | undefined,
    rowData: any,
    columnField: string,
    resolvedRowId: string,
    rawValue: unknown
  ): unknown {
    if (!columnDef?.renderCell) {
      return value;
    }

    try {
      const renderParams = {
        id: resolvedRowId,
        field: columnField,
        row: rowData,
        value: rawValue,
        formattedValue: value,
        colDef: columnDef
      } as InputTableCellParams;

      const renderedValue = columnDef.renderCell(renderParams);
      return this.extractValueFromRenderedElement(renderedValue, rowData, columnField);
    } catch (error) {
      console.warn(`Failed to resolve renderCell value for field "${columnField}"`, error);
      return value;
    }
  }

  /**
   * Extracts the actual value from a rendered React element
   */
  private static extractValueFromRenderedElement(renderedValue: any, rowData: any, columnField: string): unknown {
    if (!isValidElement(renderedValue)) {
      return typeof renderedValue === 'string' || typeof renderedValue === 'number' ? renderedValue : renderedValue;
    }

    const props = renderedValue.props as any;

    // Handle value prop
    if (props?.value !== undefined) {
      return this.handleValueProp(props.value, props.options);
    }

    // Handle columnDisplay prop
    if (props?.columnDisplay) {
      return this.handleColumnDisplayProp(props.columnDisplay, rowData, columnField);
    }

    // Handle children prop
    if (typeof props?.children === 'string' || typeof props?.children === 'number') {
      return props.children;
    }

    return renderedValue;
  }

  /**
   * Handles value prop with optional options mapping
   */
  private static handleValueProp(value: unknown, options?: any[]): unknown {
    if (!Array.isArray(options) || options.length === 0) {
      return value;
    }

    const mapOptionValue = (optionValue: unknown) => {
      const matchedOption = options.find(
        (opt: { value?: unknown; label?: unknown }) =>
          opt && opt.value !== undefined && String(opt.value) === String(optionValue)
      );
      return matchedOption?.label ?? optionValue;
    };

    if (Array.isArray(value)) {
      return value.map(mapOptionValue).join(', ');
    }

    return mapOptionValue(value);
  }

  /**
   * Handles columnDisplay prop for data extraction
   */
  private static handleColumnDisplayProp(displayKey: string, rowData: any, columnField: string): unknown {
    const rowRecord = rowData as Record<string, unknown>;
    const dataKey = `${columnField}_data`;
    const dataValue = (rowRecord?.[dataKey] as Record<string, unknown> | undefined)?.[displayKey];

    return dataValue !== undefined ? dataValue : rowRecord[columnField];
  }
}
